import { AnnouncementType, type IAnnouncementAudience, type IAnnouncementLocalization } from '../types/announcement';

/** The first broken rule; the form shows its text (hooks/useValidationText after task 7). */
export type TValidationReason =
  | 'validation.oneDefault'
  | 'validation.duplicateLanguage'
  | 'validation.emptyText'
  | 'validation.version'
  | 'validation.versionFormat'
  | 'validation.publishInFuture'
  | 'validation.endInFuture'
  | 'validation.endRequired'
  | 'validation.endAfterPublish'
  | 'validation.ctaBoth'
  | 'validation.ctaHttps'
  | 'validation.audienceVersion'
  | 'validation.audienceVersionOrder';

/** Mirrors CreateDraft/UpdateDraft validation of notificationservice; returns the first broken rule. */
export function validate(localizations: IAnnouncementLocalization[]): TValidationReason | null {
  if (localizations.filter(l => l.isDefault).length !== 1) return 'validation.oneDefault';
  if (new Set(localizations.map(l => l.language)).size !== localizations.length) return 'validation.duplicateLanguage';
  if (localizations.some(l => !l.title.trim() || !l.text.trim())) return 'validation.emptyText';
  return null;
}

const NEEDS_END = [AnnouncementType.ANNOUNCEMENT_TYPE_PROMO, AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE];

function isHttps(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export interface IAnnouncementInput {
  type: AnnouncementType;
  localizations: IAnnouncementLocalization[];
  publishAt?: Date;
  endAt?: Date;
  audience?: IAnnouncementAudience;
}

/** Mirrors CreateDraft/UpdateDraft/EditPublished validation of notificationservice; first broken rule. */
export function validateAnnouncement({
  type,
  localizations,
  publishAt,
  endAt,
  audience,
}: IAnnouncementInput): TValidationReason | null {
  const base = validate(localizations);
  if (base) return base;
  for (const l of localizations) {
    const label = l.ctaLabel?.trim() ?? '';
    const url = l.ctaUrl?.trim() ?? '';
    if (!label !== !url) return 'validation.ctaBoth';
    if (url && !isHttps(url)) return 'validation.ctaHttps';
  }
  if (NEEDS_END.includes(type) && !endAt) return 'validation.endRequired';
  if (endAt && publishAt && endAt.getTime() <= publishAt.getTime()) return 'validation.endAfterPublish';
  // As the service: a schedule time and an end date in the past would publish nothing or hide at once.
  if (publishAt && publishAt.getTime() <= Date.now()) return 'validation.publishInFuture';
  if (endAt && endAt.getTime() <= Date.now()) return 'validation.endInFuture';
  return validateAudience(audience);
}

const VERSION = /^\d+(\.\d+){0,3}$/;

/** Same business version as the service compares them: missing parts count as zero (1.22 = 1.22.0). */
export function sameVersion(a: string, b: string): boolean {
  const key = (v: string) => {
    const s = v.trim();
    return VERSION.test(s) ? [...s.split('.'), '0', '0', '0'].slice(0, 4).map(Number).join('.') : s;
  };
  return key(a) === key(b);
}

/** A business version as the service accepts it: 1–4 dotted numeric parts, each 0–999. */
export const isBusinessVersion = (v: string): boolean =>
  VERSION.test(v) && v.split('.').every(part => Number(part) <= 999);

const versionParts = (v: string): number[] => [...v.split('.').map(Number), 0, 0, 0].slice(0, 4);

/** Audience bounds (trimmed, unset when blank): each a business version, from ≤ to, inclusive. */
export function validateAudience(audience: IAnnouncementAudience | undefined): TValidationReason | null {
  const min = audience?.versionMin;
  const max = audience?.versionMax;
  if ((min && !isBusinessVersion(min)) || (max && !isBusinessVersion(max))) return 'validation.audienceVersion';
  if (min && max) {
    const from = versionParts(min);
    const to = versionParts(max);
    const i = from.findIndex((n, k) => n !== to[k]);
    if (i >= 0 && from[i] > to[i]) return 'validation.audienceVersionOrder';
  }
  return null;
}
